// @flow
import * as React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Icon, useToast } from '@locus-taxy/neo-ui';

import TimeUtils, { DateTimeFormat } from 'common/utils/TimeUtils';
import { ExportStatus } from 'common/enums/ExportEnums';
import LocusApiModel from 'common/modules/api/LocusApiModel';
import useClientId from 'common/hooks/useClientId';

const POLL_INTERVAL = 1500;

type UseExportAsyncReturn = (params: InitExportParams) => Promise<void>;

type UseExportAsyncOptions = {
    exportApi: LocusApiModel,
    exportStatusApi: LocusApiModel,
    entityName: string,
    timeout?: number,
    retainPollingOnClose?: boolean,
    showExportId?: boolean,
    onSuccess?: () => void,
    onError?: () => void,
    onCloseToast?: () => void
};

type KeyValuePair = {
    [k: string]: any
};

type InitExportParams = {
    params: KeyValuePair,
    requestBody: KeyValuePair
};

const useAsyncExport = ({
    exportApi,
    exportStatusApi,
    entityName,
    timeout,
    showExportId = true, // show export id in the toast
    retainPollingOnClose, // denotes whether to continue polling when user closes the queueToast
    onSuccess, // called the status is 'DONE' after polling
    onError, // called on error while calling export / export status api
    onCloseToast // called when user closes the queue toast
}: UseExportAsyncOptions): UseExportAsyncReturn => {
    const timerRef = React.useRef();
    const isMountedRef = React.useRef(true);
    const activeToastRef = React.useRef();

    const clientId = useClientId();
    const { showToast, closeToast } = useToast();
    const { t } = useTranslation('entityManagement');

    const exportTranslatePrefix = 'entityExport.exportToast';

    React.useEffect(() => {
        return () => {
            isMountedRef.current = false;

            // stopping the polling when the component un-mounts, since it
            // has few edge cases to handle if we retain. will enhance later.
            if (activeToastRef.current) {
                closeToast(activeToastRef.current);
            }

            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
        // closeToast is changing reference every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const showErrorToast = ({
        error
    }: {
        exportId?: string,
        error?: Error
    }) => {
        activeToastRef.current = showToast({
            title: 'Download failed',
            palette: 'danger',
            icon: <Icon icon="alert_hex" size="xLarge" color="red.400" />,
            content: error?.message
        });
    };

    const showQueuedToast = ({
        exportId,
        email
    }: {
        exportId?: string,
        email: string
    }) => {
        activeToastRef.current = showToast({
            title: t(`${exportTranslatePrefix}.message.queued`, {
                exportId,
                master: entityName
            }),
            palette: 'success',
            // to prevent auto hiding
            autoHideDuration: null,
            icon: <Icon icon="check_filled" size="xLarge" color="green.400" />,
            content: t(`${exportTranslatePrefix}.subText.queued`, {
                email
            }),
            onClose: () => {
                if (!retainPollingOnClose) {
                    clearInterval(timerRef.current);
                }

                if (isMountedRef.current) {
                    onCloseToast?.();
                }
            }
        });
    };

    const showSuccessToast = ({
        exportId,
        reportURL
    }: {
        exportId: string,
        reportURL: string
    }) => {
        activeToastRef.current = showToast({
            title: (
                <Trans
                    i18nKey={`entityManagement:${exportTranslatePrefix}.message.success`}
                >
                    Your export {showExportId ? <b>{{ exportId }}</b> : ''} for
                    {{ master: entityName }} is ready.
                </Trans>
            ),
            // to prevent auto hiding
            autoHideDuration: null,
            palette: 'success',
            icon: <Icon icon="check_filled" size="xLarge" color="green.400" />,
            content: (
                <Trans
                    i18nKey={`entityManagement:${exportTranslatePrefix}.subText.success`}
                >
                    To download click <a href={reportURL}>here</a>
                </Trans>
            )
        });
    };

    const pollExportStatus = (exportId: string) => {
        const timer = setInterval(async () => {
            try {
                const {
                    status: { status },
                    reportURL,
                    statusUpdates
                } = await exportStatusApi.fetch({
                    clientId,
                    exportId
                });

                const pollSoFar = TimeUtils.diff({
                    startDate: statusUpdates[0].updatedOn,
                    endDate: TimeUtils.now(true, DateTimeFormat.UTC_FORMAT),
                    unit: 'm'
                });

                if (status === ExportStatus.QUEUED) {
                    if (timeout && pollSoFar > timeout) {
                        clearInterval(timer);
                        closeToast(activeToastRef.current);

                        if (isMountedRef.current) {
                            onError?.();
                        }
                    }
                } else if (status === ExportStatus.DONE) {
                    clearInterval(timer);
                    closeToast(activeToastRef.current);
                    showSuccessToast({ exportId, reportURL });

                    if (isMountedRef.current) {
                        onSuccess?.();
                    }
                } else {
                    clearInterval(timer);
                    closeToast(activeToastRef.current);

                    if (isMountedRef.current) {
                        onError?.();
                    }
                }
            } catch (error) {
                clearInterval(timer);
                showErrorToast({ exportId, error });

                if (isMountedRef.current) {
                    onError?.();
                }
            }
        }, POLL_INTERVAL);

        timerRef.current = timer;
    };

    return async ({ params, requestBody }: InitExportParams) => {
        try {
            // closing the previous toast on multiple calls
            if (activeToastRef.current) {
                closeToast(activeToastRef.current);
            }

            const { emailAddresses } = requestBody;
            const email = emailAddresses?.[0].emailAddress;

            // show toast once we start export
            showQueuedToast({ email });

            const requestParams = { ...params, clientId };
            const { exportId } = await exportApi.post(
                requestParams,
                requestBody
            );

            closeToast(activeToastRef.current);
            // show toast with exportId until polling is done
            showQueuedToast({ exportId, email });

            pollExportStatus(exportId);
        } catch (error) {
            closeToast(activeToastRef.current);
            showErrorToast({ error });

            if (isMountedRef.current) {
                onError?.();
            }
        }
    };
};

export default useAsyncExport;
